"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import { Link } from "@/i18n/navigation";
import { useLocale, useTranslations } from "next-intl";
import { toast } from "react-toastify";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogTitle,
} from "../ui/dialog";
import ProgressButton from "@/components/ui/Button/ProgressButton";

const formatRelativeTime = (dateString, locale, t) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return null;
    const diffMs = Date.now() - date.getTime();
    const seconds = Math.max(0, Math.floor(diffMs / 1000));
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    const weeks = Math.floor(days / 7);
    const months = Math.floor(days / 30);
    const years = Math.floor(days / 365);

    if (seconds < 60) return t("just_now");
    if (minutes < 60) return t("minutes_ago", { count: minutes });
    if (hours < 24) return t("hours_ago", { count: hours });
    if (days < 7) return t("days_ago", { count: days });
    if (weeks < 4) return t("weeks_ago", { count: weeks });
    if (months < 12) return t("months_ago", { count: months });
    return t("years_ago", { count: years });
};

const buildItemLink = (item) => {
    const courseData = item.product || item.program;
    if (!courseData?.slug) return "/courses";

    if (
        courseData.programConstructorVersion &&
        courseData.programConstructorVersion >= 2
    ) {
        return `/accelerators/${courseData.slug}/dashboard`;
    }

    const isProgram = item._kind === "student" || item.category === "program" || item.program;
    if (isProgram) {
        if (courseData.slug === "le-programme-rocket-2020") {
            return `/course/${courseData.slug}/1/1`;
        }
        return `/course/${courseData.slug}/dashboard`;
    }

    return `/course/${courseData.slug}/1/1`;
};

const ArchivedItem = ({ item, index, onUnarchived }) => {
    const t = useTranslations("CourseSection");
    const locale = useLocale();
    const [isLoading, setIsLoading] = useState(false);
    const [isLeaving, setIsLeaving] = useState(false);

    const courseData = item.product || item.program;
    if (!courseData) return null;

    const isStudent = item._kind === "student";
    const link = buildItemLink(item);
    const relativeArchivedAt = formatRelativeTime(item.archivedAt, locale, t);
    const kindLabel = isStudent
        ? t("kind_accelerator")
        : courseData.programConstructorVersion >= 2 || item.category === "program" || item.program
            ? t("kind_program")
            : t("kind_course");

    const handleUnarchive = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (isLoading) return;
        setIsLoading(true);

        const endpoint = isStudent
            ? "/api/user/put/archive-accelerator"
            : "/api/user/put/archive-course";
        const body = isStudent
            ? { studentId: item.id, archived: false }
            : { buyerId: item.id, archived: false };

        try {
            const response = await fetch(endpoint, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(body),
            });
            const data = await response.json();

            if (!response.ok) {
                toast.error(data?.message || t("archive_error"));
                setIsLoading(false);
            } else {
                toast.success(t("unarchive_success"));
                setIsLeaving(true);
                setTimeout(() => {
                    if (onUnarchived) onUnarchived(item._uid);
                }, 220);
            }
        } catch (err) {
            toast.error(t("archive_error"));
            setIsLoading(false);
        }
    };

    return (
        <div
            className={`group relative flex items-center gap-3 px-3 py-2.5 rounded-xl border border-gray-200 dark:border-borderDark bg-white dark:bg-bgDark-840/60 transition-all duration-200 hover:border-primary/40 hover:shadow-sm hover:bg-gray-50/60 dark:hover:bg-bgDark-840 ${isLeaving ? "opacity-0 -translate-x-2 scale-[0.98]" : "opacity-100"}`}
            style={{
                transitionProperty: isLeaving
                    ? "opacity, transform"
                    : "border-color, background-color, box-shadow",
                animation: `archived-item-in 0.35s ease-out ${Math.min(index, 7) * 35}ms both`,
            }}
        >
            <Link
                href={link}
                className="absolute inset-0 z-0 rounded-xl"
                aria-label={`${courseData.name} – ${t("open_course")}`}
            />

            <div className="relative pointer-events-none w-11 h-11 rounded-lg overflow-hidden shrink-0 ring-1 ring-gray-200/70 dark:ring-borderDark/70">
                {courseData.image ? (
                    <Image
                        src={courseData.image}
                        alt={courseData.name}
                        width={44}
                        height={44}
                        className="object-cover w-full h-full"
                    />
                ) : (
                    <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 dark:from-grayDark-800 dark:to-grayDark-900" />
                )}
            </div>

            <div className="relative pointer-events-none flex-1 min-w-0 pr-1">
                <h4 className="font-semibold text-gray-900 dark:text-white text-sm leading-tight truncate mb-0 group-hover:text-primary dark:group-hover:text-primary transition-colors">
                    {courseData.name}
                </h4>
                <div className="flex items-center gap-1.5 mt-1 text-xs text-gray-500 dark:text-textSecondDark mb-0">
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded-md bg-gray-100 dark:bg-bgDark-800 text-[10px] font-medium uppercase tracking-wide text-gray-600 dark:text-grayDark-300 shrink-0">
                        {kindLabel}
                    </span>
                    {relativeArchivedAt && (
                        <>
                            <span className="text-gray-300 dark:text-grayDark-600 shrink-0">·</span>
                            <span className="truncate">
                                {t("archived_at", { time: relativeArchivedAt })}
                            </span>
                        </>
                    )}
                </div>
            </div>

            <ProgressButton
                type="button"
                onClick={handleUnarchive}
                disabled={isLoading || isLeaving}
                loading={isLoading}
                fillBg="#E8511D"
                fillText="#ffffff"
                aria-label={t("unarchive")}
                title={t("unarchive")}
                overlayClassName="inline-flex items-center justify-center w-9 h-9 sm:w-auto sm:h-auto sm:gap-1.5 sm:px-3 sm:py-1.5 text-sm font-medium"
                className="relative z-10 inline-flex items-center justify-center w-9 h-9 sm:w-auto sm:h-auto sm:gap-1.5 sm:px-3 sm:py-1.5 rounded-lg text-sm font-medium text-primary bg-primary/5 hover:bg-primary/10 active:bg-primary/15 disabled:cursor-not-allowed transition-colors shrink-0"
            >
                <SolarIcon name="restart-linear" className="shrink-0 text-[14px]" />
                <span className="hidden sm:inline">{t("unarchive")}</span>
            </ProgressButton>
        </div>
    );
};

const SkeletonItem = () => (
    <div className="flex items-center gap-3 px-3 py-2.5 rounded-xl border border-gray-200 dark:border-borderDark bg-white dark:bg-bgDark-840/60">
        <div className="w-11 h-11 rounded-lg bg-gray-100 dark:bg-bgDark-800 animate-pulse shrink-0" />
        <div className="flex-1 space-y-2 min-w-0">
            <div className="h-3 bg-gray-100 dark:bg-bgDark-800 rounded animate-pulse w-3/4" />
            <div className="h-2 bg-gray-100 dark:bg-bgDark-800 rounded animate-pulse w-1/2" />
        </div>
        <div className="w-9 h-9 sm:w-[110px] sm:h-8 bg-gray-100 dark:bg-bgDark-800 rounded-lg animate-pulse shrink-0" />
    </div>
);

const EmptyState = () => {
    const t = useTranslations("CourseSection");
    return (
        <div className="text-center py-10 px-4">
            <div className="relative w-20 h-20 mx-auto mb-5">
                <div className="absolute -inset-3 rounded-[2rem] border-2 border-dashed border-gray-200 dark:border-borderDark/60 opacity-60" />
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-gray-50 to-gray-100 dark:from-bgDark-840 dark:to-bgDark-800 shadow-inner" />
                <div className="relative w-full h-full flex items-center justify-center">
                    <SolarIcon name="inbox-linear" className="text-gray-400 dark:text-grayDark-500 text-[26px]" />
                </div>
            </div>
            <h3 className="font-semibold text-gray-900 dark:text-white text-sm mb-1">
                {t("archived_empty_title")}
            </h3>
            <p className="text-xs text-gray-500 dark:text-textSecondDark max-w-[16rem] mx-auto mb-0 leading-relaxed">
                {t("archived_empty")}
            </p>
        </div>
    );
};

export default function ArchivedCoursesModal({ isOpen, onClose, onUnarchive }) {
    const t = useTranslations("CourseSection");
    const [isLoading, setIsLoading] = useState(false);
    const [items, setItems] = useState([]);
    const [hasFetched, setHasFetched] = useState(false);

    const fetchArchives = async () => {
        setIsLoading(true);
        try {
            const response = await fetch("/api/user/get/archived-courses", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({}),
            });
            const data = await response.json();

            if (!response.ok) {
                toast.error(data?.message || t("archive_load_error"));
                setItems([]);
            } else {
                const tagged = [
                    ...(data.archivedCourses || []).map((it) => ({
                        ...it,
                        _kind: "buyer",
                        _uid: `buyer-${it.id}`,
                    })),
                    ...(data.archivedPrograms || []).map((it) => ({
                        ...it,
                        _kind: "buyer",
                        _uid: `buyer-${it.id}`,
                    })),
                    ...(data.archivedAccelerators || []).map((it) => ({
                        ...it,
                        _kind: "student",
                        _uid: `student-${it.id}`,
                    })),
                ];
                tagged.sort((a, b) => {
                    const da = a.archivedAt ? new Date(a.archivedAt).getTime() : 0;
                    const db = b.archivedAt ? new Date(b.archivedAt).getTime() : 0;
                    return db - da;
                });
                setItems(tagged);
            }
        } catch (err) {
            toast.error(t("archive_load_error"));
            setItems([]);
        } finally {
            setIsLoading(false);
            setHasFetched(true);
        }
    };

    useEffect(() => {
        if (isOpen) {
            fetchArchives();
        } else {
            setHasFetched(false);
            setItems([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const handleUnarchived = (uid) => {
        setItems((prev) => prev.filter((it) => it._uid !== uid));
        if (onUnarchive) onUnarchive();
    };

    const showList = !isLoading && items.length > 0;
    const showEmpty = !isLoading && hasFetched && items.length === 0;

    return (
        <Dialog open={isOpen} onOpenChange={(open) => !open && onClose && onClose()}>
            <DialogContent className="archived-modal max-w-xl p-0 gap-0 overflow-hidden border border-gray-200 dark:border-borderDark">
                <style jsx global>{`
                    @keyframes archived-item-in {
                        from {
                            opacity: 0;
                            transform: translateY(6px);
                        }
                        to {
                            opacity: 1;
                            transform: translateY(0);
                        }
                    }
                    @keyframes archived-modal-slide-up {
                        from {
                            transform: translateY(100%);
                        }
                        to {
                            transform: translateY(0);
                        }
                    }
                    @keyframes archived-modal-slide-down {
                        from {
                            transform: translateY(0);
                        }
                        to {
                            transform: translateY(100%);
                        }
                    }
                    @media (max-width: 639.98px) {
                        .archived-modal {
                            width: 100% !important;
                            max-width: 100% !important;
                            max-height: 92vh !important;
                            left: 0 !important;
                            right: 0 !important;
                            top: auto !important;
                            bottom: 0 !important;
                            transform: none !important;
                            border-bottom-left-radius: 0 !important;
                            border-bottom-right-radius: 0 !important;
                            border-top-left-radius: 1.25rem !important;
                            border-top-right-radius: 1.25rem !important;
                            border-bottom-width: 0 !important;
                            animation: archived-modal-slide-up 0.32s cubic-bezier(0.16, 1, 0.3, 1) !important;
                        }
                        .archived-modal[data-state="closed"] {
                            animation: archived-modal-slide-down 0.22s ease-in !important;
                        }
                        .archived-modal-body {
                            max-height: calc(92vh - 96px) !important;
                            padding-bottom: max(1rem, env(safe-area-inset-bottom)) !important;
                        }
                    }
                `}</style>

                <div className="sm:hidden flex justify-center pt-2.5 pb-1 shrink-0">
                    <div className="w-10 h-1 rounded-full bg-gray-300 dark:bg-grayDark-700" />
                </div>

                <div className="relative px-6 pt-5 sm:pt-6 pb-4 border-b border-gray-100 dark:border-borderDark/70">
                    <div
                        aria-hidden="true"
                        className="absolute -top-12 -right-12 w-32 h-32 rounded-full bg-primary/5 blur-2xl pointer-events-none"
                    />
                    <div className="relative flex items-start gap-3">
                        <div className="w-10 h-10 rounded-xl bg-primary/10 ring-1 ring-primary/20 flex items-center justify-center shrink-0">
                            <SolarIcon name="archive-linear" className="text-primary text-[18px]" />
                        </div>
                        <div className="flex-1 min-w-0 pr-8">
                            <DialogTitle className="font-bold text-gray-900 dark:text-white text-lg leading-tight mb-0 tracking-tight">
                                {t("archived_title")}
                            </DialogTitle>
                            <DialogDescription className="text-xs text-gray-500 dark:text-textSecondDark mt-1 mb-0 leading-relaxed">
                                {showList
                                    ? t("archived_count", { count: items.length })
                                    : t("archived_subtitle")}
                            </DialogDescription>
                        </div>
                    </div>
                </div>

                <div
                    className="archived-modal-body px-4 py-4 overflow-y-auto"
                    style={{ maxHeight: "min(60vh, 480px)" }}
                >
                    {isLoading ? (
                        <div className="space-y-2">
                            <SkeletonItem />
                            <SkeletonItem />
                            <SkeletonItem />
                        </div>
                    ) : showEmpty ? (
                        <EmptyState />
                    ) : (
                        <div className="space-y-2">
                            {items.map((item, index) => (
                                <ArchivedItem
                                    key={item._uid}
                                    item={item}
                                    index={index}
                                    onUnarchived={handleUnarchived}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}
